import React, { useEffect, useState } from 'react';
import { Modal, ModalBody, ModalFooter } from 'reactstrap';
import Tabs from '../../../components/tabs';
import CustomButton from '../../../components/customButton';
import CustomConfirm from '../../../components/customConfirm';
import PortalInfo from '../sections/portalInfo';
import PortalCampaigns from '../sections/portalCampaigns';
import PortalUsers from '../sections/portalUsers';
import DeletePortalModal from './deletePortalModal';
import PortalFeatures, { normalizeFeatures } from '../features';
import API from '../../../services/api';
import { useDispatch } from 'react-redux';
import { setAlert } from '../../../redux/dashboard/actions';

const ManagePortalModal = ({ portal, isOpen, toggle, onCancel, reloadCallback }) => {
  const [newDisplayName, setNewDisplayName] = useState(portal?.displayName);
  const [newDescription, setNewDescription] = useState(portal?.description);
  const [newActiveFeatures, setNewActiveFeatures] = useState(
    normalizeFeatures(portal?.activeFeatures ?? portal?.features ?? portal?.feature)
  );
  const [selectedTab, setSelectedTab] = useState(0);
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const dispatch = useDispatch();

  useEffect(() => {
    setNewDisplayName(portal?.displayName);
    setNewDescription(portal?.description);
    setNewActiveFeatures(normalizeFeatures(portal?.activeFeatures ?? portal?.features ?? portal?.feature));
    setSelectedTab(0);
  }, [portal, isOpen]);

  const handleDelete = () => {
    handlePortalDelete(portal.id);
    setLoadingSubmit(true);
    setShowDeleteDialog(false);
    toggle();
  };

  const handlePortalDelete = portalId => {
    setLoadingSubmit(true);
    API.deletePortal(portalId)
      .then(() => {
        dispatch(setAlert({ type: 'success', message: 'Portal deleted successfully!' }));
        reloadCallback();
      })
      .catch(error => {
        dispatch(setAlert({ type: 'danger', message: `Error deleting portal: ${error.message}` }));
      })
      .finally(() => {
        setShowDeleteDialog(false);
        setLoadingSubmit(false);
      });
  };

  const onConfirm = () => {
    if (selectedTab === 0) {
      setLoadingSubmit(true);
      const updatedPortal = { ...portal, displayName: newDisplayName, description: newDescription };
      if (newDisplayName !== portal.displayName || newDescription !== portal.description) {
        API.updatePortal(updatedPortal, ['displayName', 'description'])
          .then(() => {
            dispatch(setAlert({ type: 'success', message: 'Portal updated successfully!' }));
            reloadCallback();
          })
          .catch(error => {
            dispatch(setAlert({ type: 'danger', message: `Error updating portal: ${error.message}` }));
          })
          .finally(() => {
            toggle(false);
            setLoadingSubmit(false);
          });
      }
    } else if (selectedTab === 3) {
      setLoadingSubmit(true);
      const updatedPortal = { ...portal, activeFeatures: newActiveFeatures };

      API.updatePortal(updatedPortal, ['activeFeatures'])
        .then(() => {
          dispatch(setAlert({ type: 'success', message: 'Portal features updated successfully!' }));
          reloadCallback();
        })
        .catch(error => {
          dispatch(setAlert({ type: 'danger', message: `Error updating portal features: ${error.message}` }));
        })
        .finally(() => {
          toggle(false);
          setLoadingSubmit(false);
        });
    } else {
      reloadCallback();
      toggle(false);
    }
  };

  const canConfirm = () => {
    if (!portal) return false;
    if (selectedTab === 0) {
      return newDisplayName !== portal.displayName || newDescription !== portal.description;
    } else if (selectedTab === 1) {
      return true;
    } else if (selectedTab === 3) {
      const activeFeatures = normalizeFeatures(portal.activeFeatures ?? portal.features ?? portal.feature);
      return (
        activeFeatures.length !== newActiveFeatures.length ||
        activeFeatures.some(feature => !newActiveFeatures.includes(feature))
      );
    } else {
      return false;
    }
  };

  return (
    <Modal size="xl" id="manage-portal-modal" isOpen={isOpen} fade={false} centered>
      <ModalBody>
        <Tabs
          classes="manage-portal-tabs"
          onTabsChange={setSelectedTab}
          selectedTab={selectedTab}
          pages={{
            tabs: [
              {
                name: 'Info',
                content: (
                  <div>
                    <h2 className="section-header">Editing Info on {portal.displayName}</h2>{' '}
                    <h4 className="section-label">Configure name and description on {portal.displayName}</h4>
                    <PortalInfo
                      portal={portal}
                      reloadCallback={reloadCallback}
                      onChange={portal => {
                        setNewDisplayName(portal.displayName);
                        setNewDescription(portal.description);
                      }}
                    />
                  </div>
                )
              },
              {
                name: 'Campaigns',
                content: (
                  <div>
                    <h2 className="section-header">Campaigns on {portal.displayName}</h2>{' '}
                    <h4 className="section-label">View campaigns on {portal.displayName}</h4>
                    <PortalCampaigns portal={portal} />
                  </div>
                )
              },
              {
                name: 'Users',
                content: (
                  <div>
                    <h2 className="section-header">Users on {portal.displayName}</h2>
                    <h4 className="section-label">Manage users on {portal.displayName}</h4>
                    <PortalUsers portal={portal} />
                  </div>
                )
              },
              {
                name: 'Attribution',
                content: (
                  <div>
                    <h2 className="section-header">Features on {portal.displayName}</h2>
                    <h4 className="section-label">Manage features on {portal.displayName}</h4>
                    <PortalFeatures features={newActiveFeatures} onSelection={setNewActiveFeatures} />
                  </div>
                )
              }
            ]
          }}
        />
        {showDeleteDialog && (
          <DeletePortalModal
            isOpen={showDeleteDialog}
            cancelCb={() => setShowDeleteDialog(false)}
            confirmCb={() => handlePortalDelete(portal.id)}
          />
        )}
      </ModalBody>
      <ModalFooter>
        <button className="btn btn-secondary btn-danger" onClick={() => setShowDeleteDialog(true)}>
          <i className="uil uil-trash" />
          Delete
        </button>
        <CustomButton
          handleClick={onCancel ? onCancel : toggle}
          classes="btn-secondary"
          title="Cancel"
          disabled={false}
        />
        <CustomButton
          handleClick={() => {
            onConfirm();
          }}
          classes="btn-primary"
          title="Confirm changes"
          loading={loadingSubmit}
          disabled={!canConfirm()}
        />
        <CustomConfirm
          messageType="deletePortal"
          messageContext={portal}
          confirmCb={() => handleDelete()}
          cancelCb={() => setShowDeleteDialog(false)}
          isOpen={showDeleteDialog}
        />
      </ModalFooter>
    </Modal>
  );
};

export default ManagePortalModal;
